/**
 * The person's limits, as the engine read them (AC-2) — the detail behind the
 * band (owner, 22 Oct 2026: the pair broken apart, the band kept clean):
 *
 *   • why one trip can last what the band says — "why 179 and not 183" — when
 *     the title has two limits on absence and one leaves less
 *     (`inoutAnswerWhy`);
 *   • the title's limits on absence first, one card per row, each named as
 *     its meter on the band ("In total · 8 months in the permit") and drawn as
 *     its rule reads (`InOutLimitDrawings`: the total on its window's line,
 *     the limit in a row trip by trip);
 *   • the other rules after them — the 90/180, a minimum stay — side by side
 *     where there is room ("Other rules"), most relevant first;
 *   • one mark: the rule the band's number is read from, with the answer's
 *     hourglass (`inoutAnswerClock`) — never a second "first".
 *
 * In every card (the overview's language — the hourglass, the days left in
 * large):
 *
 *   • the rule's hourglass and name, the law behind it one tap away (ⓘ,
 *     `InOutRuleInfo`), and its state only when it is not within its limit;
 *   • the days left in large, a bar of how much of the limit is taken from
 *     today, and what is counted ("183 of 244 days");
 *   • days still to confirm (the person's to settle) as a margin, ±N, with the
 *     way to settle them;
 *   • a rule two norms read apart (conflict A): the bar runs to the farther
 *     reading, the band between the two drawn as risk — never as margin —
 *     and said in words, with a note when the person is in it;
 *   • a rule still being read (UNRESOLVED): no number and no action — why,
 *     and whose it is to settle, on the card;
 *   • on the rule people know as "the 90/180", where it applies and where it
 *     does not (`InOutShortStayExplainer`).
 *
 * Rules that are not this person's (NOT_APPLICABLE) are left out. Where the
 * days to confirm are settled is the page's: the dashboard's notices; the
 * public calculator has no record to settle them in (`settleHref: null`).
 *
 * A closed InOut's limits (`asOf`, its closing day — H7) are read as of that
 * day: every "today" a card, a drawing or the why says becomes that day.
 */
import { AlertTriangle, CheckCircle2, CircleDot, Hourglass, Info } from '@tonnie/assets';
import {
  INOUT_CLOCK_DAYS_UNIT_KEY,
  INOUT_CLOCK_STATUS_KEY,
  INOUT_UNRESOLVED_OWNER_KEY,
  inoutClockCountedText,
  inoutClockFigures,
  inoutClockHasNumber,
  inoutClockNameKey,
  inoutClockLeftPercent,
  inoutClockUsedPercent,
  inoutIsShortStayRule,
  inoutShownClocks,
  inoutAbsenceLimits,
  inoutAnswer,
  inoutAnswerClock,
  inoutAnswerWhy,
  inoutLimitMeterText,
  inoutAnswerWhyText,
  inoutExhaustsText,
  inoutInRiskZoneText,
  inoutMarginText,
  inoutNoRuleText,
  type InOutPastDay,
  type InOutClock,
  type InOutWhereToday,
  type InOutClockFigures,
  type InOutClockStatus,
} from '@tonnie/heroes';
import { useCountryName, useLocale } from '@tonnie/locale';
import type { InOutReading } from '@tonnie/sdk';
import type { InOutExceptionInfo } from '@tonnie/types';
import type { Tone } from '@tonnie/design-tokens';
import { Alert, AlertDescription, Badge, Card, Progress, cn } from '@tonnie/ui';
import { Link } from 'wouter';

import { INOUT_CLASSES, INOUT_TONE } from '../lib/inout-tone';
import { InOutHourglass, inoutHourglassTone } from './InOutHourglass';
import { InOutLimitLineDrawing, InOutLimitTripsDrawing } from './InOutLimitDrawings';
import { InOutRuleInfo } from './InOutRuleInfo';
import { InOutShortStayExplainer } from './InOutShortStayExplainer';

const STATUS_TONE: Record<InOutClockStatus, Tone> = {
  WITHIN: 'green',
  DEPENDS_ON_UNCERTAIN_DAYS: 'amber',
  BEYOND: 'red',
  UNRESOLVED: 'slate',
  NOT_APPLICABLE: 'slate',
};

/** The bar's fill — the state's colour, the hero's while within the limit. */
const BAR_TONE: Record<InOutClockStatus, Tone> = {
  WITHIN: INOUT_TONE,
  DEPENDS_ON_UNCERTAIN_DAYS: 'amber',
  BEYOND: 'red',
  UNRESOLVED: 'slate',
  NOT_APPLICABLE: 'slate',
};

const STATUS_ICON: Record<InOutClockStatus, typeof CheckCircle2> = {
  WITHIN: CheckCircle2,
  DEPENDS_ON_UNCERTAIN_DAYS: CircleDot,
  BEYOND: AlertTriangle,
  UNRESOLVED: Info,
  NOT_APPLICABLE: Info,
};

/**
 * A rule two norms read apart: the bar runs to the farther reading; up to the
 * nearer one is the limit, beyond it the band where the norms disagree — drawn
 * as risk, never as days left (conflict A), and said in words beneath.
 */
function RiskZoneBar({
  value,
  riskZone,
  tone,
  name,
}: {
  value: number;
  riskZone: { fromDays: number; toDays: number };
  tone: Tone;
  name: string;
}) {
  const { t, formatNumber } = useLocale();
  const nearer = (riskZone.fromDays / riskZone.toDays) * 100;
  return (
    <div className="space-y-1" data-testid="inout-clock-risk-zone">
      <Progress
        value={Math.min(value, riskZone.toDays)}
        max={riskZone.toDays}
        tone={tone}
        aria-label={name}
      />
      <div className="flex h-1.5 overflow-hidden rounded-pill" aria-hidden="true">
        <div className="bg-muted" style={{ width: `${nearer}%` }} />
        <div className="flex-1 bg-tone-amber-solid" />
      </div>
      <div className="relative h-4 text-xs tabular-nums text-muted-foreground" aria-hidden="true">
        <span className="absolute left-0">0</span>
        <span className="absolute -translate-x-1/2" style={{ left: `${nearer}%` }}>
          {formatNumber(riskZone.fromDays)}
        </span>
        <span className="absolute right-0">{formatNumber(riskZone.toDays)}</span>
      </div>
      <p className="text-xs text-foreground">
        {t('inout.clock.riskZone', {
          from: formatNumber(riskZone.fromDays),
          to: formatNumber(riskZone.toDays),
        })}
      </p>
    </div>
  );
}

/** The days a card shows in large. */
function daysOf(figures: InOutClockFigures): number | null {
  switch (figures.kind) {
    case 'total':
      return figures.left;
    case 'eachTrip':
      return figures.upTo;
    case 'needed':
      return figures.needed;
    default: {
      const unreachable: never = figures;
      throw new Error(`Unknown clock figures: ${String(unreachable)}`);
    }
  }
}

function ClockCard({
  clock,
  country,
  exceptions,
  answers,
  plannedBy,
  settleHref,
  whereToday,
  limit,
  pastDay,
}: {
  clock: InOutClock;
  country: string;
  /** Where the engine says the person is today — the 90/180's table marks it. */
  whereToday: InOutWhereToday;
  exceptions: InOutExceptionInfo[];
  /** The band's number is read from this rule — the one mark. */
  answers: boolean;
  /** Two readings of this rule disagree, and this is the stricter one, planned by. */
  plannedBy: boolean;
  settleHref: string | null;
  /** One of the title's limits on absence: named as its meter, drawn as its rule reads. */
  limit?: { evaluation: InOutReading['evaluation'] };
  /** The day a closed InOut is read as of, in words — null: today. */
  pastDay: InOutPastDay;
}) {
  const { t, formatDate, formatNumber } = useLocale();
  const Icon = STATUS_ICON[clock.status];
  const counted = inoutClockHasNumber(clock) ? clock : null;
  const name = t(inoutClockNameKey(clock), { country });
  const meter = limit ? inoutLimitMeterText(clock, t, formatNumber) : null;
  const used = counted ? inoutClockUsedPercent(counted) : 0;
  const figures = counted ? inoutClockFigures(counted) : null;
  const days = figures ? daysOf(figures) : null;

  return (
    <li data-testid="inout-clock" data-constraint={clock.constraintId} data-status={clock.status}>
      <Card
        className={cn(
          'flex h-full flex-col gap-3 p-4',
          answers && cn('border-2', INOUT_CLASSES.line)
        )}
      >
        <div className="flex items-start justify-between gap-2">
          <InOutHourglass
            size="row"
            leftPercent={counted ? inoutClockLeftPercent(counted) : 0}
            running={false}
            label={name}
            className={inoutHourglassTone(clock.status, answers)}
          />
          <div className="flex flex-wrap justify-end gap-1.5">
            {answers && (
              <Badge
                variant="outline"
                tone={INOUT_TONE}
                className="gap-1"
                data-testid="inout-clock-defines-answer"
              >
                <Hourglass className="h-3 w-3" aria-hidden="true" />
                {t('inout.meter.definesAnswer')}
              </Badge>
            )}
            {plannedBy && <Badge variant="outline">{t('inout.clock.plannedBy')}</Badge>}
            {clock.status !== 'WITHIN' && (
              <Badge
                tone={STATUS_TONE[clock.status]}
                className="gap-1 px-2 font-medium"
                data-testid="inout-clock-status"
              >
                <Icon className="h-3 w-3" aria-hidden="true" />
                {t(INOUT_CLOCK_STATUS_KEY[clock.status])}
              </Badge>
            )}
          </div>
        </div>
        {meter ? (
          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5">
              <h3 className="text-base font-semibold text-foreground">{meter.name}</h3>
              <InOutRuleInfo clock={clock} name={name} exceptions={exceptions} />
            </div>
            <p className="text-sm text-muted-foreground">
              {meter.detail ? `${name} · ${meter.detail}` : name}
            </p>
          </div>
        ) : (
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-semibold text-foreground">{name}</span>
            <InOutRuleInfo clock={clock} name={name} exceptions={exceptions} />
          </div>
        )}
        {inoutIsShortStayRule(clock) && (
          <InOutShortStayExplainer
            clock={clock}
            country={country}
            whereToday={whereToday}
            pastDay={pastDay}
          />
        )}

        {counted && figures ? (
          <div className="mt-auto space-y-2">
            <p className="flex items-baseline gap-1.5" data-testid="inout-clock-days">
              <span className="text-3xl font-semibold leading-none tabular-nums text-foreground">
                {days !== null ? formatNumber(days) : '—'}
              </span>
              <span className="text-sm text-muted-foreground">
                {t(INOUT_CLOCK_DAYS_UNIT_KEY[figures.kind])}
              </span>
            </p>
            {counted.riskZone ? (
              <RiskZoneBar
                value={counted.value}
                riskZone={counted.riskZone}
                tone={BAR_TONE[clock.status]}
                name={name}
              />
            ) : (
              <Progress value={used} tone={BAR_TONE[clock.status]} aria-label={name} />
            )}
            <p
              className="text-xs tabular-nums text-muted-foreground"
              data-testid="inout-clock-value"
            >
              {inoutClockCountedText(counted, t, formatNumber, pastDay)}
            </p>
            {limit &&
              (clock.accrual === 'CUMULATIVE' ? (
                <InOutLimitLineDrawing
                  clock={clock}
                  evaluation={limit.evaluation}
                  pastDay={pastDay}
                />
              ) : (
                <InOutLimitTripsDrawing
                  clock={clock}
                  evaluation={limit.evaluation}
                  pastDay={pastDay}
                />
              ))}
            {counted.margin !== undefined && counted.margin > 0 && (
              <p className="text-xs text-tone-amber" data-testid="inout-clock-margin">
                {inoutMarginText(counted.margin, { t, formatNumber, pastDay })}
                {settleHref && (
                  <>
                    {' '}
                    <Link href={settleHref} className="font-medium underline underline-offset-2">
                      {t('inout.clock.settleMargin')}
                    </Link>
                  </>
                )}
              </p>
            )}
            {counted.exhaustsOn && (
              <p className="text-xs text-muted-foreground">
                {inoutExhaustsText(formatDate(counted.exhaustsOn, 'calendarDay'), { t, pastDay })}
              </p>
            )}
            {counted.riskZone?.inZone && (
              <Alert
                variant="warning"
                role="note"
                className="px-3 py-2"
                data-testid="inout-clock-risk"
              >
                <AlertDescription className="text-xs">
                  {inoutInRiskZoneText(t, pastDay)}
                </AlertDescription>
              </Alert>
            )}
          </div>
        ) : (
          <div className="mt-auto space-y-1 text-sm" data-testid="inout-clock-unresolved">
            <p className="text-muted-foreground">{t('inout.clock.noNumber')}</p>
            {clock.unresolvedReason && <p className="text-foreground">{clock.unresolvedReason}</p>}
            {clock.status === 'UNRESOLVED' && (
              <p className="text-xs font-medium text-foreground">
                {t(INOUT_UNRESOLVED_OWNER_KEY[clock.unresolvedOwner ?? 'AGENCY'])}
              </p>
            )}
          </div>
        )}
      </Card>
    </li>
  );
}

export function InOutClockList({
  status,
  settleHref = '/inout/notices',
  disclaimer = true,
  asOf = null,
}: {
  status: InOutReading;
  settleHref?: string | null;
  /**
   * The day the reading is as of when it is not today — a closed InOut, read
   * as of the day it was closed (H7). Null: today, the InOut running.
   */
  asOf?: string | null;
  /** The "arithmetic, never legal advice" line — off where the frame says it already (the calculator). */
  disclaimer?: boolean;
}) {
  const { t, formatDate } = useLocale();
  const countryName = useCountryName();
  const pastDay: InOutPastDay = asOf === null ? null : formatDate(asOf, 'calendarDay');
  const { evaluation, exceptions } = status;
  const country = countryName(evaluation.country);
  const { clocks, plannedBy } = inoutShownClocks(evaluation);
  const limitIds = new Set(inoutAbsenceLimits(evaluation).map((c) => c.constraintId));
  const limits = clocks.filter((c) => limitIds.has(c.constraintId));
  const others = clocks.filter((c) => !limitIds.has(c.constraintId));
  const answerId = inoutAnswerClock(inoutAnswer(evaluation))?.constraintId ?? null;
  const card = (clock: InOutClock, limit: boolean) => (
    <ClockCard
      key={clock.constraintId}
      clock={clock}
      country={country}
      exceptions={exceptions}
      answers={clock.constraintId === answerId}
      plannedBy={plannedBy.has(clock.constraintId)}
      settleHref={settleHref}
      whereToday={evaluation.whereToday}
      limit={limit ? { evaluation } : undefined}
      pastDay={pastDay}
    />
  );
  const othersGrid = (
    <ul className="grid gap-4 @2xl:grid-cols-[repeat(auto-fit,minmax(15rem,1fr))]">
      {others.map((clock) => card(clock, false))}
    </ul>
  );

  return (
    <section className="space-y-3" aria-labelledby="inout-clocks-title" data-testid="inout-clocks">
      <h2 id="inout-clocks-title" className="text-base font-semibold text-foreground">
        {t('inout.clocks.title')}
      </h2>
      {evaluation.humanCheck ? (
        <Card className="p-4">
          <p className="text-sm text-muted-foreground" data-testid="inout-clocks-human-check">
            {t('inout.clocks.humanCheck')}
          </p>
        </Card>
      ) : clocks.length === 0 ? (
        <Card className="p-4">
          <p className="text-sm text-muted-foreground" data-testid="inout-clocks-empty">
            {inoutNoRuleText(t, pastDay)}
          </p>
        </Card>
      ) : limits.length === 0 ? (
        othersGrid
      ) : (
        <>
          <InOutAnswerWhyCard status={status} pastDay={pastDay} />
          <ul className="grid gap-4">{limits.map((clock) => card(clock, true))}</ul>
          {others.length > 0 && (
            <>
              <h3
                className="pt-2 text-sm font-semibold text-muted-foreground"
                data-testid="inout-clocks-others"
              >
                {t('inout.limits.others')}
              </h3>
              {othersGrid}
            </>
          )}
        </>
      )}
      {disclaimer && (
        <p className="text-xs text-muted-foreground">{t('inout.clocks.disclaimer')}</p>
      )}
    </section>
  );
}

/**
 * Why one trip can last what the band says (owner, 22 Oct 2026 — "why 179
 * and not 183?"): the two limits on absence, each with what it leaves, and
 * the one the answer is read from — a trip is held by the nearer of the two.
 */
function InOutAnswerWhyCard({ status, pastDay }: { status: InOutReading; pastDay: InOutPastDay }) {
  const { t, formatNumber } = useLocale();
  const countryName = useCountryName();
  const why = inoutAnswerWhy(status.evaluation);
  const definingFigures = why?.defining.figures;
  const otherFigures = why?.other.figures;
  const country = countryName(status.evaluation.country);
  const words = why ? inoutAnswerWhyText(why, { t, formatNumber, country, pastDay }) : null;
  if (!why || !definingFigures || !otherFigures || !words) return null;
  const { defining, other } = why;
  const rows = [
    { counter: other, left: otherFigures.left },
    { counter: defining, left: definingFigures.left },
  ];
  const most = Math.max(...rows.map((r) => r.left));

  return (
    <Card className={cn('space-y-3 p-4', INOUT_CLASSES.subtle)} data-testid="inout-answer-why">
      <h3 className="text-base font-semibold text-foreground">{words.title}</h3>
      <ul className="space-y-2">
        {rows.map(({ counter, left }) => {
          const { name } = inoutLimitMeterText(counter.clock, t, formatNumber);
          return (
            <li
              key={counter.clock.constraintId}
              className="grid grid-cols-[minmax(0,9rem)_minmax(0,1fr)_3rem] items-center gap-3 text-sm"
            >
              <span className="flex items-center gap-1.5 text-foreground">
                {counter.definesAnswer && (
                  <Hourglass className={cn('h-3.5 w-3.5', INOUT_CLASSES.text)} aria-hidden="true" />
                )}
                {name}
              </span>
              <span className="h-2 overflow-hidden rounded-pill bg-background" aria-hidden="true">
                <span
                  className={cn('block h-full rounded-pill', INOUT_CLASSES.solid)}
                  style={{ width: `${(left / most) * 100}%` }}
                />
              </span>
              <span className="text-right font-semibold tabular-nums text-foreground">
                {formatNumber(left)}
              </span>
            </li>
          );
        })}
      </ul>
      <p className="text-sm text-foreground">{words.body}</p>
    </Card>
  );
}
